import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../hooks/useLanguage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Globe,
  Lock,
  Sparkles,
  FolderOpen,
  Plus,
  Egg,
  Loader2,
  Boxes,
  Trash2,
  Upload,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ============================================================================
 * Logo Cyberon Kids (minecraft-style pixel logo)
 * ============================================================================ */
const CyberonKidsLogo = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 select-none"
    data-testid="cyberon-logo"
  >
    <div
      className="w-8 h-8 rounded-md flex items-center justify-center shadow-sm"
      style={{ background: 'linear-gradient(135deg, #4A9EFF 0%, #2E7BD6 100%)' }}
    >
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-white">
        <rect x="4" y="4" width="6" height="6" fill="currentColor" />
        <rect x="14" y="4" width="6" height="6" fill="currentColor" opacity="0.6" />
        <rect x="4" y="14" width="6" height="6" fill="currentColor" opacity="0.6" />
        <rect x="14" y="14" width="6" height="6" fill="currentColor" />
      </svg>
    </div>
    <span className="text-sm font-semibold text-gray-900">Cyberon Kids</span>
  </button>
);

/* ============================================================================
 * Renderiza dos círculos con los colores del Spawn Egg como referencia
 * ============================================================================ */
const SpawnEggColorDot = ({ primary, secondary, size = 24, ...rest }) => (
  <div
    className="relative rounded-full border-2 border-white shadow-sm overflow-hidden flex-shrink-0"
    style={{ width: size, height: size, background: primary }}
    title={`${primary} / ${secondary}`}
    {...rest}
  >
    <div
      className="absolute bottom-0 right-0 rounded-full"
      style={{
        width: size * 0.5,
        height: size * 0.5,
        background: secondary,
        border: '1px solid rgba(0,0,0,0.1)',
      }}
    />
  </div>
);

/* ============================================================================
 * Card de plantilla — click abre el modal de crear proyecto
 * ============================================================================ */
const TemplateCard = ({ template, onClick }) => {
  const preview = template.texture;
  const categoryLabel = {
    mob: 'Mob',
    npc: 'NPC',
    monster: 'Monstruo',
    animal: 'Animal',
    custom: 'Personalizado',
  }[template.category] || 'Modelo';

  return (
    <button
      onClick={() => onClick(template)}
      className="group relative bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-blue-500 hover:shadow-lg hover:-translate-y-0.5 transition-all text-left"
      data-testid={`template-card-${template.id}`}
    >
      <div
        className="aspect-square rounded-lg mb-3 flex items-center justify-center overflow-hidden relative"
        style={{ background: 'linear-gradient(135deg, #EBF5FF 0%, #D0E7FF 100%)' }}
      >
        {preview ? (
          <img
            src={preview}
            alt={template.name}
            style={{ imageRendering: 'pixelated', width: '75%', height: '75%', objectFit: 'contain' }}
          />
        ) : (
          <Boxes className="w-12 h-12 text-blue-400" strokeWidth={1.5} />
        )}
        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur rounded px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
          {categoryLabel}
        </div>
      </div>
      <h3 className="text-sm font-semibold text-gray-900 truncate">
        {template.name}
      </h3>
      <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
        {template.description_es || 'Plantilla'}
      </p>
    </button>
  );
};

/* ============================================================================
 * "Plantilla vacía" card — crear desde cero sin plantilla
 * ============================================================================ */
const EmptyProjectCard = ({ onClick }) => (
  <button
    onClick={onClick}
    className="group bg-white border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
    data-testid="template-card-empty"
  >
    <div
      className="aspect-square rounded-lg mb-3 flex items-center justify-center border-2 border-dashed border-blue-300 bg-blue-50/50 group-hover:bg-white transition-colors"
    >
      <Plus className="w-10 h-10 text-blue-500" strokeWidth={2} />
    </div>
    <h3 className="text-sm font-semibold text-gray-900">
      Empezar en blanco
    </h3>
    <p className="text-xs text-gray-500 mt-0.5">
      Modelo vacío sin plantilla
    </p>
  </button>
);

/* ============================================================================
 * Card de proyecto existente
 * ============================================================================ */
const ProjectCard = ({ project, onClick, onDelete }) => {
  const primary = project.spawn_egg_color_primary || '#4A9EFF';
  const secondary = project.spawn_egg_color_secondary || '#FFFFFF';
  // Prioridad: cover_image (render 3D del mob) > texture (mapa plano)
  const preview = project.cover_image || project.texture;
  const isRender = !!project.cover_image;
  const updatedDate = project.updated_at ? new Date(project.updated_at) : null;

  return (
    <div
      className="group relative bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-blue-500 hover:shadow-lg hover:-translate-y-0.5 transition-all"
      data-testid={`project-card-${project.id}`}
    >
      <button
        onClick={() => onClick(project)}
        className="w-full text-left"
      >
        <div
          className="aspect-square rounded-lg mb-3 flex items-center justify-center overflow-hidden relative"
          style={{ background: 'linear-gradient(135deg, #EBF5FF 0%, #D0E7FF 100%)' }}
        >
          {preview ? (
            <img
              src={preview}
              alt={project.name}
              style={{ imageRendering: isRender ? 'auto' : 'pixelated', width: isRender ? '100%' : '75%', height: isRender ? '100%' : '75%', objectFit: 'contain' }}
            />
          ) : (
            <Boxes className="w-12 h-12 text-blue-400" strokeWidth={1.5} />
          )}
          <div className="absolute bottom-2 right-2">
            <SpawnEggColorDot primary={primary} secondary={secondary} size={22} />
          </div>
        </div>
        <h3 className="text-sm font-semibold text-gray-900 truncate">
          {project.name}
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">
          {project.author}
          {updatedDate && ` · ${updatedDate.toLocaleDateString()}`}
        </p>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(project);
        }}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1.5 rounded-md bg-white/90 backdrop-blur border border-gray-200 text-red-500 hover:bg-red-50 transition-all"
        title="Eliminar"
        data-testid={`delete-project-${project.id}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

/* ============================================================================
 * Modal de creación de proyecto (Spawn Egg configurable)
 * ============================================================================ */
const CreateProjectDialog = ({ open, onClose, template, onCreated }) => {
  const [name, setName] = useState('');
  const [author, setAuthor] = useState('');
  const [category, setCategory] = useState('custom');
  const [primaryColor, setPrimaryColor] = useState('#4A9EFF');
  const [secondaryColor, setSecondaryColor] = useState('#FFFFFF');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      setName(template ? `${template.name} de mi versión` : '');
      setAuthor('');
      setCategory(template?.category || 'custom');
      setPrimaryColor(template?.spawn_egg_color_primary || '#4A9EFF');
      setSecondaryColor(template?.spawn_egg_color_secondary || '#FFFFFF');
    }
  }, [open, template]);

  const handleCreate = async () => {
    if (!name.trim() || !author.trim()) {
      toast.error('Nombre y autor son obligatorios');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        name: name.trim(),
        author: author.trim(),
        category,
        template_id: template?.id || null,
        spawn_egg_color_primary: primaryColor,
        spawn_egg_color_secondary: secondaryColor,
        // NO enviamos spawn_egg — Minecraft usa su textura nativa y aplica
        // base_color + overlay_color desde el client_entity.json
      };
      const res = await axios.post(`${API}/projects`, payload);
      toast.success('Proyecto creado');
      onCreated(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Error al crear el proyecto', {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-blue-600" />
            Nuevo proyecto
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {template && (
            <div className="flex items-center gap-3 p-2.5 bg-blue-50 rounded-lg border border-blue-100">
              {template.texture ? (
                <img
                  src={template.texture}
                  alt=""
                  style={{ imageRendering: 'pixelated', width: 32, height: 32 }}
                />
              ) : (
                <Boxes className="w-8 h-8 text-blue-500" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs text-blue-700 font-medium">Plantilla</div>
                <div className="text-sm text-blue-900 font-semibold truncate">{template.name}</div>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs font-medium text-gray-700 mb-1 block">
              Nombre <span className="text-red-500">*</span>
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Mi criatura mágica"
              className="h-9 border-gray-200"
              data-testid="create-project-name"
              autoFocus
            />
          </div>

          <div>
            <Label className="text-xs font-medium text-gray-700 mb-1 block">
              Autor <span className="text-red-500">*</span>
            </Label>
            <Input
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="Tu nombre"
              className="h-9 border-gray-200"
              data-testid="create-project-author"
            />
          </div>

          <div>
            <Label className="text-xs font-medium text-gray-700 mb-1 block">
              Categoría
            </Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9 border-gray-200" data-testid="create-project-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Personalizado</SelectItem>
                <SelectItem value="mob">Mob</SelectItem>
                <SelectItem value="npc">NPC</SelectItem>
                <SelectItem value="monster">Monstruo</SelectItem>
                <SelectItem value="animal">Animal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Spawn Egg - Colores nativos de Minecraft */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Egg className="w-4 h-4 text-blue-600" />
              <Label className="text-xs font-semibold text-gray-800">
                Huevo de aparición (Spawn Egg)
              </Label>
            </div>
            <p className="text-[11px] text-gray-500 mb-3 leading-relaxed">
              Minecraft usará su textura oficial del huevo y la teñirá automáticamente con estos dos colores.
            </p>
            <div className="flex gap-3 items-start">
              {/* Vista de referencia con dos círculos */}
              <div
                className="w-16 h-16 rounded-lg border-2 border-gray-200 flex items-center justify-center flex-shrink-0"
                style={{ background: 'repeating-conic-gradient(#f3f4f6 0% 25%, #ffffff 0% 50%) 50% / 10px 10px' }}
              >
                <SpawnEggColorDot
                  primary={primaryColor}
                  secondary={secondaryColor}
                  size={40}
                  data-testid="spawn-egg-preview"
                />
              </div>
              {/* Colors */}
              <div className="flex-1 space-y-2">
                <div>
                  <Label className="text-[11px] text-gray-600 mb-0.5 block">Color primario</Label>
                  <div className="flex gap-1.5">
                    <input
                      type="color"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
                      data-testid="spawn-egg-primary"
                    />
                    <Input
                      value={primaryColor.toUpperCase()}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="h-8 text-xs font-mono border-gray-200 flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] text-gray-600 mb-0.5 block">Color secundario (puntos)</Label>
                  <div className="flex gap-1.5">
                    <input
                      type="color"
                      value={secondaryColor}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
                      data-testid="spawn-egg-secondary"
                    />
                    <Input
                      value={secondaryColor.toUpperCase()}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      className="h-8 text-xs font-mono border-gray-200 flex-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={creating}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="create-project-submit"
          >
            {creating && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
            Crear proyecto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ============================================================================
 * HomePage principal
 * ============================================================================ */
const HomePage = () => {
  const navigate = useNavigate();
  const { currentLanguage, changeLanguage, languages } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setIsAdmin(sessionStorage.getItem('cyberon_admin') === 'true');
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tRes, pRes] = await Promise.all([
        axios.get(`${API}/templates`),
        axios.get(`${API}/projects`),
      ]);
      setTemplates(tRes.data.filter((t) => t.is_active !== false));
      setProjects(pRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleLogoClick = () => {
    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);
    if (newCount >= 5) {
      setLogoClickCount(0);
      setAdminDialogOpen(true);
    }
    setTimeout(() => setLogoClickCount(0), 3000);
  };

  const handleAdminAccess = () => {
    if (adminPassword === 'admin') {
      setAdminDialogOpen(false);
      setAdminPassword('');
      sessionStorage.setItem('cyberon_admin', 'true');
      navigate('/admin');
    } else {
      toast.error('Contraseña incorrecta');
      setAdminPassword('');
    }
  };

  const openCreateDialog = (template) => {
    setSelectedTemplate(template);
    setDialogOpen(true);
  };

  const handleProjectCreated = (project) => {
    setDialogOpen(false);
    navigate(`/editor/${project.id}`);
  };

  const handleOpenProject = (project) => {
    navigate(`/editor/${project.id}`);
  };

  const handleDeleteProject = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setDeleting(true);
    try {
      await axios.delete(`${API}/projects/${id}`);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      toast.success('Proyecto eliminado', { description: pendingDelete.name });
      setPendingDelete(null);
    } catch (err) {
      console.error(err);
      toast.error('Error al eliminar', {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleImportProject = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const res = await axios.post(`${API}/projects/import`, data);
      setProjects((prev) => [res.data, ...prev]);
      toast.success(`Proyecto "${res.data.name}" importado`);
    } catch (err) {
      console.error(err);
      toast.error('Error al importar', {
        description: 'Selecciona un archivo .json exportado desde Cyberon',
      });
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50/30 via-white to-blue-50/40" data-testid="home-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <CyberonKidsLogo onClick={handleLogoClick} />
            <div className="h-6 w-px bg-gray-200" />
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">Beta</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-400" />
            <Select value={currentLanguage} onValueChange={changeLanguage}>
              <SelectTrigger className="w-[120px] h-8 border-gray-200 text-xs" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-14 pb-10">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100/60 border border-blue-200 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs font-medium text-blue-700">
              Crea tu propio addon de Minecraft
            </span>
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 tracking-tight leading-none mb-4">
            Minecraft
            <span
              className="ml-2 bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(90deg, #4A9EFF 0%, #2E7BD6 100%)' }}
            >
              Creator
            </span>
          </h1>
          <p className="text-base text-gray-600 max-w-2xl leading-relaxed">
            Diseña criaturas, personajes y objetos para Minecraft Education y Bedrock sin
            necesidad de programar. Elige una plantilla, personalízala y exporta tu addon
            listo para jugar.
          </p>
        </div>
      </section>

      {/* Crear Proyecto - Galería de plantillas */}
      <section className="max-w-7xl mx-auto px-6 pb-10">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-1 h-6 rounded-full bg-blue-500" />
              <h2 className="text-2xl font-bold text-gray-900">Crear proyecto</h2>
            </div>
            <p className="text-sm text-gray-500 ml-3">
              Elige una plantilla o empieza desde cero
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/templates')}
            className={`${isAdmin ? 'hidden sm:flex' : 'hidden'} border-gray-200 h-8`}
            data-testid="manage-templates-btn"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Gestionar plantillas
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="aspect-[3/4] rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <EmptyProjectCard onClick={() => openCreateDialog(null)} />
            {templates.map((t) => (
              <TemplateCard key={t.id} template={t} onClick={openCreateDialog} />
            ))}
          </div>
        )}
      </section>

      {/* Mis Proyectos */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-1 h-6 rounded-full bg-blue-500" />
              <h2 className="text-2xl font-bold text-gray-900">Mis proyectos</h2>
              {projects.length > 0 && (
                <button
                  onClick={() => navigate('/projects')}
                  className="ml-3 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
                  data-testid="see-all-projects-link"
                >
                  Ver todos <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <p className="text-sm text-gray-500 ml-3">
              {projects.length === 0
                ? 'Todavía no tienes proyectos. Crea uno arriba'
                : `${projects.length} proyecto${projects.length === 1 ? '' : 's'} guardado${projects.length === 1 ? '' : 's'}`}
            </p>
          </div>
          <div>
            <input
              ref={importInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleImportProject}
              data-testid="import-project-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              className="border-gray-200 h-8"
              data-testid="import-project-btn"
            >
              {importing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
              Importar proyecto
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="aspect-[3/4] rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-gray-200 rounded-xl py-14 flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
              <FolderOpen className="w-7 h-7 text-blue-400" strokeWidth={1.5} />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700">Aún no hay proyectos</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Elige una plantilla arriba para empezar
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {projects.map((p) => (
              <ProjectCard
                key={p.id}
                project={p}
                onClick={handleOpenProject}
                onDelete={(project) => setPendingDelete(project)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between text-xs text-gray-500">
          <div>Cyberon Kids · Minecraft Education Edition</div>
          <div className="flex items-center gap-3">
            <span>v1.5.0</span>
            <button
              onClick={() => setAdminDialogOpen(true)}
              className="opacity-30 hover:opacity-100 transition-opacity p-1"
              data-testid="admin-access-button"
            >
              <Lock className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </footer>

      {/* Confirmación borrar proyecto */}
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && !deleting && setPendingDelete(null)}
      >
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-gray-900">
              <Trash2 className="w-5 h-5 text-red-500" />
              ¿Eliminar este proyecto?
            </AlertDialogTitle>
            <AlertDialogDescription className="pt-1">
              Se eliminará <span className="font-semibold text-gray-900">"{pendingDelete?.name}"</span> de forma permanente. No podrás recuperarlo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="confirm-delete-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProject}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white focus:ring-red-500"
              data-testid="confirm-delete-confirm"
            >
              {deleting && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Project Dialog */}
      <CreateProjectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        template={selectedTemplate}
        onCreated={handleProjectCreated}
      />

      {/* Admin Password Dialog */}
      <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-blue-600" />
              Acceso administrador
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Introduce la contraseña para acceder al panel de administrador.
            </p>
            <Input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdminAccess()}
              placeholder="••••••"
              className="border-gray-200"
              autoFocus
              data-testid="admin-password-input"
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setAdminDialogOpen(false);
                  setAdminPassword('');
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAdminAccess}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="admin-enter-button"
              >
                Entrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
